import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Award } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/StatCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const empty = { name: "", description: "" };

export default function MasterGrades() {
  const { can } = useAuth(); const isAdmin = can("master_manage");
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/grades").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((p) => ({ ...p, data: { ...p.data, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { name: form.data.name, description: form.data.description };
    try {
      if (form.item) await api.put(`/grades/${form.item.id}`, payload); else await api.post("/grades", payload);
      toast.success("Grade disimpan"); setForm(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Hapus grade ${g.name}?`)) return;
    try { await api.delete(`/grades/${g.id}`); toast.success("Grade dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="grades-page">
      <PageHeader title="Master Grade" subtitle="Tingkat kualitas ikan yang dipilih saat stok masuk dan tercetak di label QR">
        {isAdmin && <Button className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setForm({ item: null, data: empty })} data-testid="btn-add-grade"><Plus className="h-4 w-4 mr-1" /> Tambah Grade</Button>}
      </PageHeader>
      <div className="card-aqua overflow-hidden rise rise-1">
        <table className="w-full text-sm" data-testid="table-grades">
          <thead className="table-head"><tr><th className="text-left px-4 py-3">Grade</th><th className="text-left px-4 py-3">Keterangan</th><th className="px-4 py-3 text-right">Aksi</th></tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={3} className="text-center py-10 text-slate-500" data-testid="grades-empty">Belum ada grade.</td></tr>}
            {list.map((g, idx) => (
              <tr key={g.id} className="border-t border-sky-50 hover:bg-sky-50/50" data-testid={`grade-row-${idx}`}>
                <td className="px-4 py-3 font-semibold text-slate-800"><span className="inline-flex items-center gap-2"><Award className="h-4 w-4 text-violet-600" />{g.name}</span></td>
                <td className="px-4 py-3 text-slate-600">{g.description || "—"}</td>
                <td className="px-4 py-3 text-right">{isAdmin && (
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-sky-700" onClick={() => setForm({ item: g, data: { ...empty, ...g } })} data-testid={`btn-edit-grade-${idx}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => remove(g)} data-testid={`btn-delete-grade-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="grade-form-dialog">
          <DialogHeader><DialogTitle>{form?.item ? "Edit Grade" : "Tambah Grade"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5"><Label>Nama grade</Label><Input value={form.data.name} onChange={(e) => set("name", e.target.value)} required maxLength={40} placeholder="cth. A, Super, Ekspor" data-testid="input-grade-name" /></div>
              <div className="space-y-1.5"><Label>Keterangan</Label><Input value={form.data.description} onChange={(e) => set("description", e.target.value)} placeholder="Opsional" data-testid="input-grade-description" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForm(null)} data-testid="btn-grade-cancel">Batal</Button>
                <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-grade-submit">{saving ? "Menyimpan..." : "Simpan"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
